import type { FormEvent } from "react";
import type { EventId, PlanEvent } from "../../../../shared/types";
import type { AddEventFormProps } from "../../types/AddEventFormProps";
import type { useEventFormState } from "../../hooks/useEventFormState";
import { apiUpload } from "../../../../lib/api/client";
import { queryClient } from "../../../../lib/query-client";
import type { ImageDTO } from "../../../../shared/schemas";

export function getConflictingEvent(
  existingEvents: PlanEvent[] | undefined,
  date: string,
  startTime: string,
  endTime: string,
): PlanEvent | null {
  if (!existingEvents || existingEvents.length === 0) return null;
  const eventOnDate = existingEvents.filter((e) => e.date === date);
  for (const existing of eventOnDate) {
    if (startTime < existing.endTime && endTime > existing.startTime) {
      return existing;
    }
  }
  return null;
}

interface UseAddEventFormSubmitParams {
  readonly isEditing: boolean;
  readonly eventToEdit: PlanEvent | undefined;
  readonly state: ReturnType<typeof useEventFormState>;
  readonly onAdd: AddEventFormProps["onAdd"];
  readonly onAddEvent: AddEventFormProps["onAddEvent"];
  readonly onUpdateEvent: AddEventFormProps["onUpdateEvent"];
  readonly onClose: () => void;
  readonly pendingFiles: File[];
}

// Fire-and-forget: runs completely outside React lifecycle
function scheduleImageUploads(eventId: string, files: File[]) {
  setTimeout(async () => {
    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("eventId", eventId);
      try {
        await apiUpload<ImageDTO>("/images", formData);
      } catch (err) {
        console.error("[images] pending upload failed:", err);
      }
    }
    // Refresh image queries after all uploads
    queryClient.invalidateQueries({ queryKey: ["images"] });
    queryClient.invalidateQueries({ queryKey: ["image-quota"] });
  }, 0);
}

export function useAddEventFormSubmit({
  isEditing,
  eventToEdit,
  state,
  onAdd,
  onAddEvent,
  onUpdateEvent,
  onClose,
  pendingFiles,
}: UseAddEventFormSubmitParams) {
  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmed = state.title.trim();
    if (!trimmed) return;

    // Snapshot files before component unmounts
    const filesToUpload = [...pendingFiles];
    let targetEventId: string | undefined;

    if (isEditing && eventToEdit && onUpdateEvent) {
      onUpdateEvent(eventToEdit.id as EventId, {
        title: trimmed,
        category: state.category,
        date: state.date,
        startTime: state.startTime,
        endTime: state.endTime,
        allDay: state.allDay,
        description: state.description,
        notes: state.notes,
        tags: state.tags,
        recurrence: state.recurrence,
        reminderMinutes: state.reminderMinutes,
        reminderTime: state.reminderTime,
        reminderType: state.reminderType,
        eventSubtasks: state.eventSubtasks,
      });
      targetEventId = String(eventToEdit.id);
    } else if (onAddEvent) {
      const newId = onAddEvent({
        title: trimmed,
        category: state.category,
        date: state.date,
        startTime: state.startTime,
        endTime: state.endTime,
        allDay: state.allDay,
        description: state.description,
        notes: state.notes,
        tags: state.tags,
        recurrence: state.recurrence,
        reminderMinutes: state.reminderMinutes,
        reminderTime: null,
        reminderType: "BEFORE",
        eventSubtasks: state.eventSubtasks,
      });
      if (newId) targetEventId = String(newId);
    } else {
      onAdd(
        trimmed,
        state.category,
        state.date,
        state.startTime,
        state.endTime,
      );
    }

    // Schedule uploads BEFORE close — they run in setTimeout, outside React
    if (targetEventId && filesToUpload.length > 0) {
      scheduleImageUploads(targetEventId, filesToUpload);
    }

    onClose();
  }

  return { handleSubmit };
}
